import { AuthService } from '@/modules/auth/services';
import * as AT from '@/modules/auth/store/actionTypes';
import { IAuthState } from '@/modules/auth/store/state';
import Auth from '@/packages/Auth';
import { IRootState } from '@/store/state';
import { ActionContext, ActionTree } from 'vuex';
import {
  LoginFormData,
  SignupRequest,
  VerifyTfaRequest,
  GenerateTfaRequest,
  ForgotPasswordRequest,
  GenerateLoginOtpRequest,
  ValidateLoginOtpRequest
} from '../types';

/**
 * --------------------------------------------------
 *  Auth Actions
 * --------------------------------------------------
 **/
export const AuthActions: IAuthActions = {
  [AT.LOGIN_USER]: async ({ commit }, data: LoginFormData) => {
    const response = await AuthService.login(data);
    if (response && response.token) {
      Auth.setTokenAndUserDetails(response.token, response?.refreshToken);
    }

    return response;
  },

  [AT.VERIFY_TOKEN]: async ({ commit }, token: string) => {
    const response = await AuthService.checkToken(token);
    return response;
  },

  [AT.RESET_PASSWORD]: async ({ commit }, data: LoginFormData) => {
    const response = await AuthService.resetPassword(data);
    return response;
  },

  [AT.FORGOT_PASSWORD]: async (
    { commit },
    forgotPasswordRequest: ForgotPasswordRequest
  ) => {
    const response = await AuthService.forgotPassword(forgotPasswordRequest);
    return response;
  },

  [AT.SIGNUP]: async ({ commit }, signupRequest: SignupRequest) => {
    const response = await AuthService.signup(signupRequest);
    return response;
  },

  [AT.VALIDATE_INVITATION_CODE]: async ({ commit }, invitationCode: string) => {
    const response = await AuthService.validateInvitationCode(invitationCode);
    return response;
  },

  [AT.IMPERSONATION_LOGIN]: async (
    { commit },
    impersonationToken: string
  ): Promise<string | null> => {
    const response = await AuthService.impersonate(impersonationToken);
    const token = response?.data?.token;

    if (token) {
      const user = JSON.parse(atob(token.split('.')[1]));
      Auth.setToken(token);
      Auth.setUserId(user.id);
      Auth.setUserIsImpersonated('y');
    }

    return Promise.resolve(response?.data?.token);
  },

  [AT.VERIFY_TFA_CODE]: async ({ commit }, verifyRequest: VerifyTfaRequest) => {
    const response = await AuthService.verifyTfa(verifyRequest);
    if (response && response.token) {
      Auth.setTokenAndUserDetails(response.token, response?.refreshToken);
    }
    return response;
  },

  [AT.GENERATE_TFA_CODE]: async (
    { commit },
    generateRequest: GenerateTfaRequest
  ) => {
    const response = await AuthService.generateTfa(generateRequest);
    return response;
  },

  [AT.VALIDATE_RETRY_TOKEN]: async (
    { commit },
    retryToken: string
  ): Promise<Response> => {
    return await AuthService.validateRetryToken(retryToken);
  },

  [AT.GENERATE_LOGIN_OTP]: async (
    { commit },
    generateLoginOtpRequest: GenerateLoginOtpRequest
  ) => {
    const response = await AuthService.generateLoginOtp(
      generateLoginOtpRequest
    );
    return response;
  },

  [AT.VALIDATE_LOGIN_OTP]: async (
    { commit },
    validateLoginOtpRequest: ValidateLoginOtpRequest
  ) => {
    const response = await AuthService.validateLoginOtp(
      validateLoginOtpRequest
    );
    if (response && response.token) {
      Auth.setTokenAndUserDetails(response.token, response?.refreshToken);
    }
    return response;
  },

  [AT.GENERATE_LOGIN_LINK]: async ({ commit }, email: string) => {
    const response = await AuthService.generateLoginLink(email);
    return response;
  },

  [AT.VALIDATE_LOGIN_LINK]: async ({ commit }, token: string) => {
    const response = await AuthService.validateLoginLink(token);
    if (response && response.token) {
      Auth.setTokenAndUserDetails(response.token, response?.refreshToken);
    }
    return response;
  }
};

/**
 * --------------------------------------------------
 *  Auth Action Context Interface
 * --------------------------------------------------
 **/
export interface IAuthActionContext
  extends ActionContext<IAuthState, IRootState> {}

/**
 * --------------------------------------------------
 *  Auth Actions Interface
 * --------------------------------------------------
 **/
export interface IAuthActions extends ActionTree<IAuthState, IRootState> {
  [AT.LOGIN_USER](context: IAuthActionContext, data: LoginFormData): void;
  [AT.VERIFY_TOKEN](context: IAuthActionContext, token: string): void;
  [AT.RESET_PASSWORD](context: IAuthActionContext, data: any): void;
  [AT.IMPERSONATION_LOGIN](
    context: IAuthActionContext,
    token: string
  ): Promise<string | null>;
  [AT.FORGOT_PASSWORD](
    context: IAuthActionContext,
    forgotPasswordRequest: ForgotPasswordRequest
  ): void;
  [AT.SIGNUP](context: IAuthActionContext, signupRequest: SignupRequest): void;
  [AT.VALIDATE_INVITATION_CODE](
    context: IAuthActionContext,
    invitationCode: string
  ): void;
  [AT.VERIFY_TFA_CODE](
    context: IAuthActionContext,
    verifyRequest: VerifyTfaRequest
  ): Promise<string | null>;
  [AT.GENERATE_TFA_CODE](
    context: IAuthActionContext,
    generateRequest: GenerateTfaRequest
  ): Promise<Response>;
  [AT.VALIDATE_RETRY_TOKEN](
    context: IAuthActionContext,
    retryToken: string
  ): void;
  [AT.GENERATE_LOGIN_OTP](
    context: IAuthActionContext,
    generateLoginOtpRequest: GenerateLoginOtpRequest
  ): Promise<Response>;
  [AT.VALIDATE_LOGIN_OTP](
    context: IAuthActionContext,
    validateLoginOtp: ValidateLoginOtpRequest
  ): Promise<Response>;
  [AT.GENERATE_LOGIN_LINK](
    context: IAuthActionContext,
    email: string
  ): Promise<Response>;
  [AT.VALIDATE_LOGIN_LINK](
    context: IAuthActionContext,
    token: string
  ): Promise<Response>;
}
